import React, { useRef, useState } from "react";
import { Icon, Search, Typography, Tooltip } from "top-brains-ui";
import "./HeaderMenuDropdown.scss";
import classNames from "classnames";
import { HeaderMenuDropdownProps, optionsType } from "./types";
import { capitalizeFirstLetter, truncateText } from "@src/utils/CommonUtils";
import useClickOutside from "@src/hooks/useClickOutside";

const HeaderMenuDropdown = ({
  menuDropdownOptionClick = () => {},
  menuDropdownLabelClick = () => {},
  onSubMenuClick = () => {},
  selected,
  options,
  subMenuList,
  selectedOption = {
    label: "",
    value: "",
    iconName: "",
  },
  selectedSubMenu,
  placeholder,
  loadMore,
  hasMore,
  isLoading,
}: HeaderMenuDropdownProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [closeTimeout, setCloseTimeout] = useState<number | null>(null);

  const closeOptions = () => {
    setShowOptions(false);
  };

  const handleMouseEnter = () => {
    setShowOptions(true);
  };

  const dropDownMouseEnter = () => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
  };

  const handleSelectOption = (option: optionsType) => {
    closeOptions();
    menuDropdownOptionClick(option);
    setSearchValue("");
  };

  const [searchValue, setSearchValue] = useState("");

  useClickOutside(optionsRef, closeOptions);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;

    if (
      target.scrollHeight - target.scrollTop <= target.clientHeight + 10 &&
      hasMore &&
      !isLoading
    ) {
      loadMore?.();
    }
  };

  return (
    <div
      className={classNames("header-menu-dropdown", {
        ["header-menu-dropdown--selected"]: selected,
      })}
    >
      <div
        className={classNames("header-menu")}
        // onMouseEnter={handleMouseEnter}
        // onMouseLeave={handleMouseLeave}
      >
        <div>
          <div className={classNames("header-menu-container")}>
            <Typography
              as={"div"}
              lineHeight={"18px"}
              fontSize={12}
              onClick={menuDropdownLabelClick}
            >
              {selectedOption?.label?.length <= 12
                ? selectedOption.label
                : truncateText(selectedOption?.label, 8)}
            </Typography>
            <div className={classNames("label-icon")}>
              <Icon
                onClick={handleMouseEnter}
                name={showOptions ? "arrows_top_icon" : "arrows_down_icon"}
                color="var(--primary-icon-color)"
                height={8}
                width={8}
                hoverEffect={false}
              />
            </div>
          </div>
        </div>
        {showOptions && (
          <div
            className={classNames("header-menu-dropdown-container")}
            ref={optionsRef}
            onMouseEnter={dropDownMouseEnter}
          >
            <div className="header-menu-search-container">
              <Search
                onSearch={() => {}}
                value={searchValue}
                isExpand={true}
                onClose={() => {}}
                onExpand={() => {}}
                showClose={false}
                placeholder={placeholder}
              />
            </div>
            <div className={classNames("option-card")}>
              {options.map(
                (option, index) =>
                  index === 0 && (
                    <div
                      key={index}
                      onClick={() => handleSelectOption(option)}
                      className={classNames("header-menu-options", {
                        ["header-menu-selected-option"]:
                          selectedOption.label === option.label,
                      })}
                    >
                      <Typography
                        key={index}
                        as={"div"}
                        lineHeight={"30px"}
                        className="header-menu-label"
                      >
                        {option.label.length > 25 ? (
                          <Tooltip
                            title={option.label}
                            placement="bottom"
                          >
                            <span>
                              {
                                option.label.substring(0, 25)
                              }
                              ...
                            </span>
                          </Tooltip>
                        ) : (
                          <span>{option.label}</span>
                        )}
                      </Typography>
                    </div>
                  ),
              )}
            </div>
            <div
              className={classNames(
                "option-card",
                `${options.length > 4 ? "scroll" : ""}`,
              )}
              onScroll={handleScroll}
            >
              {options.map(
                (option, index) =>
                  index > 0 && (
                    <div
                      key={index}
                      onClick={() => {
                        handleSelectOption(option);
                      }}
                      className={classNames("header-menu-options", {
                        ["header-menu-selected-option"]:
                          selectedOption.label === option.label,
                      })}
                    >
                      <Typography
                        key={index}
                        as={"div"}
                        lineHeight={"30px"}
                        className="header-menu-label"
                      >
                        {option?.label?.length > 25 ? (
                          <Tooltip
                            title={option.label}
                            placement="bottom"
                          >
                            <span>
                              {
                                option.label.substring(0, 25)
                              }
                              ...
                            </span>
                          </Tooltip>
                        ) : (
                          <span>{option.label}</span>
                        )}
                      </Typography>
                    </div>
                  ),
              )}
              {isLoading && (
                <div className="dropdown-loader">
                  <Typography>Loading...</Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {subMenuList && (
        <>
          {subMenuList.map((subMenuItem, index) => {
            return (
              <React.Fragment key={index}>
                {!subMenuItem.hide && (
                  <div
                    key={subMenuItem.label}
                    className="header-submenu-container"
                  >
                    <Typography
                      as="div"
                      className={classNames("header-submenu-item", {
                        ["header-submenu-item--selected"]:
                          Boolean(selectedSubMenu) &&
                          subMenuItem?.label?.toLowerCase() ===
                            selectedSubMenu?.toLowerCase(),
                      })}
                      lineHeight="18px"
                      onClick={() => onSubMenuClick(subMenuItem)}
                    >
                      {subMenuItem?.label}
                    </Typography>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
    </div>
  );
};

export default HeaderMenuDropdown;
